import { supabase } from "@/integrations/supabase/client";
import { ServiceRequest, mapToServiceRequest } from "../types";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";

export const fetchAllRequests = async (currentUserId: string) => {
  // Guard against empty or invalid user IDs
  if (!currentUserId || currentUserId.trim() === "") {
    console.error("Invalid user ID provided to fetchAllRequests");
    return [];
  }
  
  console.log(`Fetching all requests for user: ${currentUserId}`);
  
  let retryCount = 0;
  const maxRetries = 3;
  
  while (retryCount < maxRetries) {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          services (
            name,
            price,
            duration,
            duration_unit,
            instant_booking,
            requires_scheduling
          )
        `)
        .eq('user_id', currentUserId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error(`Fetch error (attempt ${retryCount + 1}/${maxRetries}):`, error);
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`Retrying in 1 second...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw error;
      }
      
      console.log(`Successfully fetched ${data?.length || 0} requests`);
      // Map the data to ensure it conforms to ServiceRequest type
      return (data || []).map(record => mapToServiceRequest(record));
    } catch (error) {
      console.error(`Fetch exception (attempt ${retryCount + 1}/${maxRetries}):`, error);
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`Retrying in 1 second...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        toast({
          variant: "destructive",
          title: "Connection Error",
          description: "Could not load your service requests. Please check your internet connection and try again."
        });
        throw error;
      }
    }
  }
  
  // If we get here, all retries failed
  return [];
};

export const fetchRequestById = async (requestId: string, currentUserId: string) => {
  let retryCount = 0;
  const maxRetries = 3;
  
  while (retryCount < maxRetries) {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          services (
            name,
            price,
            duration,
            duration_unit,
            instant_booking,
            requires_scheduling
          )
        `)
        .eq('id', requestId)
        .eq('user_id', currentUserId)
        .single();

      if (error) {
        console.error(`Fetch request error (attempt ${retryCount + 1}/${maxRetries}):`, error);
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`Retrying in 1 second...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw error;
      }
      
      // Map the data to ensure it conforms to ServiceRequest type
      return mapToServiceRequest(data);
    } catch (error) {
      console.error(`Fetch request exception (attempt ${retryCount + 1}/${maxRetries}):`, error);
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`Retrying in 1 second...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        toast({
          variant: "destructive",
          title: "Connection Error",
          description: "Could not load the service request details. Please try again."
        });
        throw error;
      }
    }
  }
  
  return null;
};

export const formatDate = (date: string) => {
  return format(new Date(date), "PPp");
};

export const acceptRequest = async (requestId: string) => {
  console.log(`Accepting request ${requestId}`);
  
  let retryCount = 0;
  const maxRetries = 3;
  
  while (retryCount < maxRetries) {
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({ status: 'accepted' })
        .eq('id', requestId);

      if (error) {
        console.error(`Accept request error (attempt ${retryCount + 1}/${maxRetries}):`, error);
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`Retrying in 1 second...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw error;
      }
      
      console.log(`Request ${requestId} accepted successfully`);
      return true;
    } catch (error) {
      console.error(`Accept request exception (attempt ${retryCount + 1}/${maxRetries}):`, error);
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`Retrying in 1 second...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        toast({
          variant: "destructive",
          title: "Connection Error",
          description: "Could not accept the service request. Please try again."
        });
        throw error;
      }
    }
  }
  
  return false;
};

export const rejectRequest = async (requestId: string, rejectionReason?: string) => {
  console.log(`Rejecting request ${requestId} with reason:`, rejectionReason);
  
  // Try with rejection_reason first, fall back to just status change if column doesn't exist
  try {
    const updateData: any = { status: 'rejected' };
    
    if (rejectionReason) {
      updateData.rejection_reason = rejectionReason;
    }
    
    const { error } = await supabase
      .from('service_requests')
      .update(updateData)
      .eq('id', requestId);

    if (error) {
      // If error might be due to missing column, try without rejection_reason
      // PGRST204 = column does not exist, 42703 = PostgreSQL undefined column error
      if (error.message.includes('rejection_reason') || error.code === '42703' || error.code === 'PGRST204') {
        console.warn('rejection_reason column not found, falling back to status-only update');
        const { error: fallbackError } = await supabase
          .from('service_requests')
          .update({ status: 'rejected' })
          .eq('id', requestId);
        
        if (fallbackError) {
          console.error("Error rejecting request (fallback):", fallbackError);
          throw fallbackError;
        }
        console.log(`Request ${requestId} rejected successfully (without reason)`);
        return; // Exit early on successful fallback
      } else {
        console.error("Error rejecting request:", error);
        throw error;
      }
    }
  } catch (error) {
    console.error("Error rejecting request:", error);
    throw error;
  }
  
  console.log(`Request ${requestId} rejected successfully`);
};

export const completeRequest = async (requestId: string) => {
  console.log(`Completing request ${requestId}`);
  const { error } = await supabase
    .from('service_requests')
    .update({ status: 'completed' })
    .eq('id', requestId);

  if (error) {
    console.error("Error completing request:", error);
    throw error;
  }
  
  console.log(`Request ${requestId} completed successfully`);
};

export const updateRequestSchedule = async (requestId: string, newDate: Date) => {
  console.log(`Updating schedule for request ${requestId} to ${newDate.toISOString()}`);
  
  let retryCount = 0;
  const maxRetries = 3;
  
  while (retryCount < maxRetries) {
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({ 
          scheduled_at: newDate.toISOString(),
          // If the request was in rejected status, move it back to accepted when rescheduled
          status: 'accepted'  
        })
        .eq('id', requestId);

      if (error) {
        console.error(`Update schedule error (attempt ${retryCount + 1}/${maxRetries}):`, error);
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`Retrying in 1 second...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw error;
      }
      
      console.log(`Schedule for request ${requestId} updated successfully`);
      return true;
    } catch (error) {
      console.error(`Update schedule exception (attempt ${retryCount + 1}/${maxRetries}):`, error);
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`Retrying in 1 second...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        toast({
          variant: "destructive",
          title: "Connection Error",
          description: "Could not update the service schedule. Please try again."
        });
        throw error;
      }
    }
  }
  
  return false;
};

export const markRequestAsPaid = async (requestId: string) => {
  console.log(`Marking request ${requestId} as paid`);
  
  let retryCount = 0;
  const maxRetries = 3;
  
  while (retryCount < maxRetries) {
    try {
      // Simplified version - just update the paid status directly
      const { data, error } = await supabase
        .from('service_requests')
        .update({ paid: true })
        .eq('id', requestId)
        .select();
        
      if (error) {
        console.error(`Error updating paid status (attempt ${retryCount + 1}/${maxRetries}):`, error);
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`Retrying in 1 second...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw error;
      }
      
      console.log(`Request ${requestId} marked as paid successfully`);
      return true;
    } catch (error) {
      console.error(`Mark as paid exception (attempt ${retryCount + 1}/${maxRetries}):`, error);
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`Retrying in 1 second...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        toast({
          variant: "destructive",
          title: "Payment Error",
          description: "Could not mark the service request as paid. Please try again."
        });
        throw error;
      }
    }
  }
  
  return false;
};

// Function to generate a shareable link for a service request
export const generateServiceRequestShareableLink = async (requestId: string) => {
  try {
    // First check if the request already has a customer_page_link
    const { data: requestData, error: requestError } = await supabase
      .from('service_requests')
      .select('customer_page_link')
      .eq('id', requestId)
      .single();
    
    if (requestError) {
      console.error('Error fetching request data:', requestError);
      return null;
    }
    
    let customerPageLink = requestData.customer_page_link;
    
    // If the request doesn't have a customer_page_link yet, generate one
    if (!customerPageLink) {
      const randomId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
      const newLink = `/service-request/${requestId}/${randomId}`;
      
      const { error: updateError } = await supabase
        .from('service_requests')
        .update({ customer_page_link: newLink })
        .eq('id', requestId);
      
      if (updateError) {
        console.error('Error updating customer page link:', updateError);
        return null;
      }
      
      customerPageLink = newLink;
    }
    
    // Create the shareable link
    const baseUrl = window.location.origin;
    const shareableLink = `${baseUrl}${customerPageLink}`;
    
    return {
      link: shareableLink,
      expiresAt: null // No expiration for customer page links
    };
  } catch (error) {
    console.error('Error generating service request shareable link:', error);
    return null;
  }
};
